import { Grid, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { FileField, FileInput, NumberInput, required } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import AdPreviewOnMobile from '../adPreviewOnMobile';

const FormUpload: React.FC<{ formData?: any }> = ({ formData }) => {

  const MAX_SIZE = 10000000; // 10MB

  const { setValue } = useFormContext();
  const fileValue = useWatch({ name: 'file' });
  const prevFileRef = useRef<string | null>(null);

  const [fileError, setFileError] = useState<string | null>(null);

  const convertBitToMb = (value: number) => {
    return `${value / 1000000}MB`;
  }

  const getVideoDuration = (file: File): Promise<number | null> => new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      const duration = video.duration;
      video.remove();
      URL.revokeObjectURL(video.src);
      resolve(duration);
    };

    video.onerror = () => {
      video.remove();
      URL.revokeObjectURL(video.src);
      resolve(null);
    };

    video.src = URL.createObjectURL(file);
  });

  useEffect(() => {
    const processFile = async () => {
      const file = fileValue?.rawFile as File | undefined;
      const currentFileId = file ? `${file.name}-${file.size}-${file.lastModified}` : null;

      if (currentFileId === prevFileRef.current) return;
      prevFileRef.current = currentFileId;

      setFileError(null);

      if (!file) {
        setValue('duration', null);
        return;
      }

      if (file.size > MAX_SIZE) {
        setValue('file', null);
        setFileError('Tamanho do arquivo excede 10 MB. Por favor, escolha um arquivo menor.');
        return;
      }

      // Auto-preencher duração para vídeos
      if (file.type.startsWith('video/')) {
        const duration = await getVideoDuration(file);
        setValue('duration', duration ? Math.ceil(duration) : 10);
      }
    };

    processFile();
  }, [fileValue, setValue]);

  const isVideoOrGif = formData?.file?.rawFile?.type?.startsWith('video/') || formData?.file?.rawFile?.type === 'image/gif';
  const hasFile = !!formData?.file;

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={8}>
          <FileInput
            source="file"
            label="Arquivo"
            accept={{ 'video/*': [], 'image/*': [] }}
            sx={{ maxWidth: 500 }}
          >
            <FileField source="src" title="title" />
          </FileInput>
          {
            fileError &&
            <Typography sx={{ marginBottom: 4, color: 'red' }}>{fileError}</Typography>
          }
          <Typography sx={{ marginBottom: 4 }}>Os vídeos serão reproduzidos sem áudio no aplicativo</Typography>
          <Typography sx={{ marginBottom: 4 }}>{`Permitido arquivos de até ${convertBitToMb(MAX_SIZE)}`}</Typography>

          {hasFile && (
            <NumberInput
              source="duration"
              label="Duração (segundos)"
              disabled={isVideoOrGif}
              format={(val: number) => val ? Math.floor(val) : val}
              isRequired
              validate={required()}
              sx={{ maxWidth: 200 }}
            />
          )}
        </Grid>
        <Grid size={4}>
          <AdPreviewOnMobile file={formData?.file} />
        </Grid>
      </Grid>
    </>
  );
}

export default FormUpload;